'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/navigation'
import { useTransition } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

const supportSchema = z.object({
  subject: z
    .string()
    .min(1, 'Por favor, descreva o assunto do seu atendimento'),
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('Por favor, insira um e-mail válido'),
  whatsapp: z
    .string()
    .min(10, 'Por favor, insira um número de WhatsApp válido'),
})

type SupportSchema = z.infer<typeof supportSchema>

type DefaultUserInfo = {
  name: string
  email: string
  whatsapp: string
}

type SupportFormProps = {
  supportPhone: string
  lockedSubject?: string | null
  defaultUserInfo?: DefaultUserInfo | null
}

export function SupportForm({
  supportPhone,
  lockedSubject,
  defaultUserInfo,
}: SupportFormProps) {
  const [isPending, startTransition] = useTransition()
  const router = useRouter()
  const isSubjectLocked = Boolean(lockedSubject)

  const form = useForm<SupportSchema>({
    resolver: zodResolver(supportSchema),
    defaultValues: {
      subject: lockedSubject ?? '',
      name: defaultUserInfo?.name ?? '',
      email: defaultUserInfo?.email ?? '',
      whatsapp: defaultUserInfo?.whatsapp ?? '',
    },
  })

  function onSubmit(values: SupportSchema) {
    startTransition(() => {
      const subjectToSend = lockedSubject ?? values.subject
      const message = `Olá, tudo bem?
Meu nome é *${values.name}* e o meu e-mail é *${values.email}* e o assunto do meu atendimento é *${subjectToSend}*`

      const whatsappUrl = new URL('https://api.whatsapp.com/send/')
      whatsappUrl.searchParams.append('phone', supportPhone)
      whatsappUrl.searchParams.append('text', message)
      whatsappUrl.searchParams.append('type', 'phone_number')
      whatsappUrl.searchParams.append('app_absent', '0')

      router.push(whatsappUrl.href)
      form.reset()
    })
  }

  return (
    <Form {...form}>
      <form className="space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
        {/* Subject Field - Full Width */}
        <FormField
          control={form.control}
          name="subject"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel className="font-semibold text-gray-800 text-sm">
                Qual será o assunto do seu atendimento?
              </FormLabel>
              <Textarea
                aria-readonly={isSubjectLocked}
                className={cn(
                  'min-h-[100px] resize-none rounded-xl border-gray-200 bg-gray-50/50 px-4 py-3 text-gray-700 transition-all duration-200 placeholder:text-gray-400 focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20',
                  isSubjectLocked &&
                    'cursor-not-allowed bg-gray-100 text-gray-600 focus:border-gray-200 focus:bg-gray-100 focus:ring-0'
                )}
                placeholder="Descreva detalhadamente o que você precisa..."
                readOnly={isSubjectLocked}
                {...field}
              />
              {isSubjectLocked && (
                <p className="text-gray-500 text-xs">
                  Assunto definido automaticamente para esse atendimento.
                </p>
              )}
              <FormMessage className="text-red-500 text-sm" />
            </FormItem>
          )}
        />

        {/* Personal Info Section */}
        <div className="space-y-6">
          <div className="border-gray-200 border-b pb-2">
            <h3 className="font-semibold text-gray-800 text-sm">
              Informações de Contato
            </h3>
          </div>

          {/* Name and Email Row */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="font-medium text-gray-700 text-sm">
                    Nome completo
                  </FormLabel>
                  <Input
                    className="rounded-xl border-gray-200 bg-gray-50/50 px-4 py-3 text-gray-700 transition-all duration-200 placeholder:text-gray-400 focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20"
                    placeholder="Digite seu nome completo"
                    {...field}
                  />
                  <FormMessage className="text-red-500 text-sm" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="font-medium text-gray-700 text-sm">
                    E-mail
                  </FormLabel>
                  <Input
                    className="rounded-xl border-gray-200 bg-gray-50/50 px-4 py-3 text-gray-700 transition-all duration-200 placeholder:text-gray-400 focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20"
                    placeholder="seu@email.com"
                    type="email"
                    {...field}
                  />
                  <FormMessage className="text-red-500 text-sm" />
                </FormItem>
              )}
            />
          </div>

          {/* WhatsApp Field - Full Width */}
          <FormField
            control={form.control}
            name="whatsapp"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel className="font-medium text-gray-700 text-sm">
                  WhatsApp
                </FormLabel>
                <Input
                  className="rounded-xl border-gray-200 bg-gray-50/50 px-4 py-3 text-gray-700 transition-all duration-200 placeholder:text-gray-400 focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20"
                  placeholder="(11) 99999-9999"
                  type="tel"
                  {...field}
                />
                <FormMessage className="text-red-500 text-sm" />
              </FormItem>
            )}
          />
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            className="w-full transform rounded-xl bg-gradient-to-r from-green-500 to-green-600 py-4 font-semibold text-base text-white shadow-lg transition-all duration-200 hover:scale-[1.02] hover:from-green-600 hover:to-green-700 hover:shadow-xl disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isPending}
            type="submit"
          >
            {isPending ? (
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>Processando...</span>
              </div>
            ) : (
              'Enviar e Abrir WhatsApp'
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
